/**
 * WhatQuiz 교사 익명 커뮤니티 메인 페이지
 * 완전 익명 보장 교사 전용 소통 공간
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/Toast';
import { communityApi, getErrorMessage } from '../../api/communityApi';
import PostList from '../../components/community/PostList';
import CreatePostForm from '../../components/community/CreatePostForm';
import LoadingSpinner from '../../components/layout/LoadingSpinner';
import './CommunityPage.css';

const CommunityPage = () => {
  const navigate = useNavigate();
  const { currentUser: user, loading: authLoading } = useAuth();
  const { showToast } = useToast();

  // 상태 관리
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [sortType, setSortType] = useState('latest');
  const [refreshing, setRefreshing] = useState(false);

  const POSTS_PER_PAGE = 20;

  /**
   * 글 목록 조회
   */
  const fetchPosts = async (page = 1, sort = 'latest', showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      }
      setError(null);

      const response = await communityApi.getPosts({
        page,
        limit: POSTS_PER_PAGE,
        sort
      });

      setPosts(response.posts || []);
      setTotalCount(response.total_count || 0);
      setHasNext(response.has_next || false);
      setCurrentPage(page);
      setSortType(sort);
    } catch (err) {
      console.error('글 목록 조회 실패:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  /**
   * 페이지 초기 로드
   */
  useEffect(() => {
    fetchPosts(1, 'latest');
  }, []);

  /**
   * 페이지 변경
   */
  const handlePageChange = (newPage) => {
    if (newPage !== currentPage && newPage >= 1) {
      fetchPosts(newPage, sortType);
      window.scrollTo(0, 0);
    }
  };

  /**
   * 새 글 작성 완료 후 목록 새로고침
   */
  const handlePostCreated = () => {
    setShowCreateForm(false);
    fetchPosts(1, sortType); // 첫 페이지로 이동하여 새 글 확인
  };

  // 새로고침 기능 제거됨

  /**
   * 글 클릭 시 상세 페이지로 이동
   */
  const handlePostClick = (postId) => {
    navigate(`/community/posts/${postId}`);
  };

  /**
   * 글 작성 버튼 클릭
   */
  const handleCreatePostClick = () => {
    if (!user) {
      // 로그인 필요 안내 후 로그인 화면으로 이동
      showToast('로그인하면 글을 쓸 수 있어요', 'info');
      navigate('/auth');
      return;
    }
    setShowCreateForm(true);
  };

  // 로딩 중
  if (authLoading) {
    return (
      <div className="community-page">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>교사 익명 커뮤니티 | 완전 익명 보장 교사 전용 소통 공간 | 왓퀴즈</title>
        <meta 
          name="description" 
          content="현직 교사만 모인 익명 커뮤니티. 수업·학급·업무 고민을 안전하게 나눠요" 
        />
        <meta 
          name="keywords" 
          content="교사 익명 커뮤니티, 교사 전용 커뮤니티, 익명 교사 소통, 교사 고민 상담, 학급 운영 고민, 교사 스트레스, 동료 교사 소통, 교사 업무 고민, 교사 커뮤니티 사이트, 교사 온라인 커뮤니티" 
        />
        <meta name="author" content="플랙티컬 주식회사" />
        <meta name="robots" content="index, follow" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://whatquiz.co.kr/community" />
        <meta property="og:site_name" content="왓퀴즈" />
        <meta property="og:title" content="교사 익명 커뮤니티 | 완전 익명 보장 교사 전용 소통 공간 | 왓퀴즈" />
        <meta 
          property="og:description" 
          content="현직 교사만 모인 익명 커뮤니티. 수업·학급·업무 고민을 안전하게 나눠요" 
        />
        <meta property="og:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        <meta property="og:image:alt" content="왓퀴즈 - 교사 익명 커뮤니티 완전 익명 보장 교사 전용 소통 공간" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="교사 익명 커뮤니티 | 완전 익명 보장 교사 전용 소통 공간 | 왓퀴즈" />
        <meta 
          name="twitter:description" 
          content="현직 교사만 모인 익명 커뮤니티. 수업·학급·업무 고민을 안전하게 나눠요" 
        />
        <meta name="twitter:image" content="https://whatquiz.co.kr/og-image-whatquiz-2025.png" />
        
        <link rel="canonical" href="https://whatquiz.co.kr/community" />
      </Helmet>
      
      <div className="community-page">
        {/* 헤더 */}
        <div className="community-header">
          <div className="header-content">
            <div className="title-section">
              <h1>
              교사 익명 커뮤니티
            </h1>
            <p className="subtitle">
              완전 익명 보장으로 자유롭게 소통하세요. 모든 글과 댓글은 "익명"으로 표시됩니다.
            </p>
          </div>

          <div className="header-actions">
            <button 
              className="btn btn-primary create-post-btn"
              onClick={handleCreatePostClick}
              disabled={loading}
            >
              글 작성하기
            </button>
          </div>
        </div>

        {/* 통계 정보 */}
        <div className="community-stats">
          <div className="stat-item">
            <span className="stat-number">{totalCount.toLocaleString()}</span>
            <span className="stat-label">총 게시글</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">∞</span>
            <span className="stat-label">익명 보장</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">24/7</span>
            <span className="stat-label">언제든 소통</span>
          </div>
        </div>
      </div>

      {/* 정렬 버튼 */}
      <div className="community-controls">
        <div className="sort-controls">
          <button
            className="sort-btn active"
            disabled={true}
          >
            최신순
          </button>
        </div>
      </div>

      {/* 글 작성 폼 모달 */}
      {showCreateForm && (
        <CreatePostForm
          onPostCreated={handlePostCreated}
          onCancel={() => setShowCreateForm(false)}
        />
      )}

      {/* 메인 콘텐츠 */}
      <div className="community-content">
        {/* 에러 메시지 (페이지 전용 클래스로 작성 모달 필드 에러와 스타일 충돌 방지) */}
        {error && (
          <div className="community-error-message">
            <span>{error}</span>
            <button onClick={() => fetchPosts(currentPage, sortType)}>
              다시 시도
            </button>
          </div>
        )}

        {/* 로딩 스피너 */}
        {loading && (
          <div className="loading-container">
            <LoadingSpinner />
            <p>글 목록을 불러오는 중...</p>
          </div>
        )}

        {/* 글 목록 */}
        {!loading && !error && (
          <>
            {posts.length > 0 ? (
              <>
                <PostList
                  posts={posts}
                  onPostClick={handlePostClick}
                  loading={refreshing}
                />

                {/* 페이지네이션 */}
                <div className="pagination">
                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1 || loading}
                  >
                    이전
                  </button>

                  <span className="page-info">
                    {currentPage} 페이지
                    {totalCount > 0 && ` (총 ${totalCount}개 글)`}
                  </span>

                  <button
                    className="page-btn"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={!hasNext || loading}
                  >
                    다음
                  </button>
                </div>
              </>
            ) : (
              /* 빈 상태 */
              <div className="empty-state">
                <h3>아직 올라온 글이 없어요</h3>
                <p>선생님의 첫 번째 이야기를 들려주세요</p>
                <button
                  className="btn btn-primary"
                  onClick={handleCreatePostClick}
                >
                  첫 번째 글 작성하기
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* 커뮤니티 가이드 */}
      <div className="community-guide">
        <h3>커뮤니티 이용 안내</h3>
        <ul>
          <li><strong>완전 익명:</strong> 모든 글과 댓글은 "익명"으로 표시됩니다</li>
          <li><strong>교사 전용:</strong> 교육 현장의 고민과 경험을 자유롭게 나누세요</li>
          <li><strong>상호 존중:</strong> 서로를 배려하는 따뜻한 소통을 지향합니다</li>
          <li><strong>개인정보 보호:</strong> 학교명이나 개인정보는 자동으로 필터링됩니다</li>
        </ul>
      </div>
    </div>
    </>
  );
};

export default CommunityPage; 